import React from "react";
import ReactApexChart from "react-apexcharts";
import { tokens } from "../../../../theme";
import { useTheme } from "@mui/material/styles";

// Function to reshape AFRR-PROPOSAL data for graphing
export const reshapeAFRRProposalData = (afrrData) => {
  if (!afrrData || !afrrData['AFRR-PROPOSAL'] || !Array.isArray(afrrData['AFRR-PROPOSAL'])) {
    return {
      capacityDown: [],
      capacityUp: [],
      priceDown: [],
      priceUp: []
    };
  }

  const capacityDown = [];
  const capacityUp = [];
  const priceDown = [];
  const priceUp = [];

  afrrData['AFRR-PROPOSAL'].forEach(entry => {
    const fromTime = new Date(entry.from).getTime();
    const toTime = new Date(entry.to).getTime();

    // Capacity data points
    capacityDown.push(
      { x: fromTime, y: entry.capacity_volume_down },
      { x: toTime, y: entry.capacity_volume_down }
    );

    capacityUp.push(
      { x: fromTime, y: entry.capacity_volume_up },
      { x: toTime, y: entry.capacity_volume_up }
    );

    // Price data points
    priceDown.push(
      { x: fromTime, y: entry.min_cap_price_aFRR_down },
      { x: toTime, y: entry.min_cap_price_aFRR_down }
    );

    priceUp.push(
      { x: fromTime, y: entry.min_cap_price_aFRR_up },
      { x: toTime, y: entry.min_cap_price_aFRR_up }
    );
  });

  return {
    capacityDown,
    capacityUp,
    priceDown,
    priceUp
  };
};

// Function to create chart series from reshaped AFRR data
export const createAFRRChartSeries = (reshapedData) => {
  return [
    {
      name: 'Capacity Volume Down (MW)',
      data: reshapedData.capacityDown,
      type: 'column',
      color: '#FF4560'
    },
    {
      name: 'Capacity Volume Up (MW)',
      data: reshapedData.capacityUp,
      type: 'column',
      color: '#505050'
    },
    {
      name: 'Min Cap Price aFRR Down (€/MW)',
      data: reshapedData.priceDown,
      type: 'column',
      color: '#FEB019',
      yAxisIndex: 1
    },
    {
      name: 'Min Cap Price aFRR Up (€/MW)',
      data: reshapedData.priceUp,
      type: 'column',
      color: '#775DD0',
      yAxisIndex: 1
    }
  ];
};

// Function to create chart options for AFRR data
export const createAFRROptions = () => {
  return {
    chart: {
      height: 350,
      zoom: { enabled: false },
      stacked: false
    },
    stroke: {
      width: [0, 0, 2, 2],
      curve: 'stepline'
    },
    plotOptions: {
      bar: {
        columnWidth: '50%'
      }
    },
    fill: {
      type: "solid",
      opacity: [0.5, 0.5, 1, 1],
      gradient: {
        inverseColors: false,
        shade: 'light',
        type: "vertical",
        opacityFrom: 0.85,
        opacityTo: 0.55,
        stops: [0, 100, 100, 100]
      }
    },
    labels: [],
    markers: {
      size: 0
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        format: 'dd MMM HH:mm'
      }
    },
    yaxis: [
      {
        title: {
          text: 'Capacity Volume (MW)',
        },
        min: 0
      },
      {
        opposite: true,
        title: {
          text: 'Price (€/MW)',
        },
        min: 0
      }
    ],
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      style: {
        fontSize: '14px'
      },
      x: {
        format: 'dd MMM yyyy HH:mm'
      },
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const x = w.globals.seriesX[seriesIndex][dataPointIndex];
        const date = new Date(x);
        const pad = n => n < 10 ? '0' + n : n;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formattedDate = `${pad(date.getDate())} ${months[date.getMonth()]} ${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        
        // Get all series values at this point in time
        let tooltipContent = '';
        for (let i = 0; i < w.config.series.length; i++) {
          const seriesData = w.config.series[i].data;
          const pointIndex = seriesData.findIndex(point => point.x === x);
          if (pointIndex !== -1 && seriesData[pointIndex].y !== null && seriesData[pointIndex].y !== undefined) {
            const seriesName = w.config.series[i].name;
            const color = w.config.series[i].color || '#FF4560';
            const value = seriesData[pointIndex].y;
            tooltipContent += `
              <div style="display:flex;text-align:center;align-items:center;gap:8px;padding:8px 10px;">
                <span style="display:inline-block;width:12px;height:12px;background:${color};border-radius:50%;margin-right:4px;"></span>
                <span style="color:#222;">${seriesName}: ${value}</span>
              </div>
            `;
          }
        }
        
        return `
          <div style="min-width:200px;">
            <div style="background:#f5f6fa;padding:8px 0 8px 0;border-radius:8px 8px 0 0;text-align:center">
              ${formattedDate}
            </div>
            ${tooltipContent}
          </div>
        `;
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      onItemClick: {
        toggleDataSeries: false
      }
    },
    title: {
      text: 'Pre Calculation Results',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    }
  };
};

const modeConfig = {
  1: { color: "#40a57b", label: "Mode 1 : CHP max power + aFRR down" },
  2: { color: "#a0befb", label: "Mode 2 : CHP e-load following" },
  3: { color: "#625a76", label: "Mode 3 : CHP technical minimum" },
  4: { color: "#f4c276", label: "Mode 4 : CHP off" },
};

export default function FilledOperationalModePreCharColumn({afrrProposalData}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  
  
  // Add null check and fallback
  if (!afrrProposalData || (!afrrProposalData['AFRR-PROPOSAL'] && !Array.isArray(afrrProposalData['AFRR-PROPOSAL']))) {
    return (
      <div style={{
        pl: 2,
        pr: 2,
        textAlign: "center",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        borderRadius: 8,
        backgroundColor: "#fff",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        pb: 4,
        height: 350,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Process the data to create time ranges
  // Handle both nested and flat data structures
  let chp01Data;
  if (afrrProposalData['AFRR-PROPOSAL'] && afrrProposalData['AFRR-PROPOSAL']) {
    chp01Data = afrrProposalData['AFRR-PROPOSAL'];
  } else if (Array.isArray(afrrProposalData)) {
    chp01Data = afrrProposalData;
  } else {
    chp01Data = [];
  }
  console.log("chp01Data", chp01Data);
  
  // Check if this is AFRR proposal data (has 'from', 'to', 'capacity_volume_down' fields)
  const isAFRRProposalData = chp01Data.length > 0 && chp01Data[0].hasOwnProperty('from') && chp01Data[0].hasOwnProperty('capacity_volume_down');
  
  if (isAFRRProposalData) {
    // This is AFRR proposal data, not CHP operational mode data
    // We should display AFRR proposal chart instead
    const reshapedData = reshapeAFRRProposalData({ 'AFRR-PROPOSAL': chp01Data });
    const series = createAFRRChartSeries(reshapedData);
    const options = createAFRROptions();
    
    return (
      <div style={{
        padding: 18,
        textAlign: "center",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        borderRadius: 8,
        backgroundColor: "#fff",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
      }}>
        <ReactApexChart options={options} series={series} height={350} />
      </div>
    );
  }
  
  // Original CHP operational mode logic
  // Find the latest data just before current hour
  const now = new Date();
  const latestData = chp01Data
    .filter(entry => new Date(entry.datetime) <= now)
    .sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0];
  
  
  // Convert point data to time ranges - create individual ranges for each data point
  const timeRanges = [];
  for (let i = 0; i < chp01Data.length; i++) {
    const currentEntry = chp01Data[i];
    const currentMode = Math.round(currentEntry.value);
    const currentTime = new Date(currentEntry.datetime);
    
    let endTime;
    
    // For each entry, create a time range from current time to next entry time
    if (i < chp01Data.length - 1) {
      const nextEntry = chp01Data[i + 1];
      endTime = new Date(nextEntry.datetime);
    } else {
      // For the last entry, add 1 hour to show it properly
      endTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
    }
    
    timeRanges.push({
      from: currentTime.toISOString(),
      to: endTime.toISOString(),
      value: 100, // Fixed value for area chart
      mode: currentMode
    });
  }


  // Create series data for each mode
  const seriesData = Object.keys(modeConfig).map(mode => {
    const modeData = timeRanges.filter(entry => entry.mode.toString() === mode);
    const points = modeData.flatMap((entry, index) => {
      const points = [
        { x: new Date(entry.from).getTime(), y: entry.value },
        { x: new Date(entry.to).getTime(), y: entry.value }
      ];
      
      // Add null point if this isn't the last entry and next entry isn't consecutive
      if (index < modeData.length - 1) {
        const nextEntry = modeData[index + 1];
        if (entry.to !== nextEntry.from) {
          points.push({ x: new Date(entry.to).getTime(), y: null });
        }
      }
      
      return points;
    });

    return {
      name: modeConfig[mode].label,
      data: points,
      color: modeConfig[mode].color
    };
  });




  const options = {
    chart: {
      type: "area",
      height: 350,
      zoom: { enabled: false }
    },
    stroke: {
      curve: "stepline",
      width: 2
    },
    fill: {
      type: "solid",
      opacity: 0.5
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        format: "dd MMM HH:mm"
      }
    },
    yaxis: {
      min: 90,
      max: 105,
      tickAmount: 10,
      labels: {
        formatter: function(value) {
          return "";
        }
      },
      title: {
        text: "Operational Mode"
      }
    },
    annotations: {
      xaxis: [
        {
          x: new Date().getTime(),
          borderColor: '#FF4560',
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            borderColor: '#FF4560',
            style: {
              color: '#fff',
              background: '#FF4560'
            },
            //show minutes 2 digits
            text: `Latest Data ${new Date(latestData.datetime).getHours()}:${new Date(latestData.datetime).getMinutes().toString().padStart(2, '0')} : Mode ${latestData.value}`
          }
        }
      ]
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      style: {
        fontSize: '14px'
      },
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const x = w.globals.seriesX[seriesIndex][dataPointIndex];
        const date = new Date(x);
        const pad = n => n < 10 ? '0' + n : n;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formattedDate = `${pad(date.getDate())} ${months[date.getMonth()]} ${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        
        // Get all series values at this point in time
        let tooltipContent = '';
        for (let i = 0; i < w.config.series.length; i++) {
          const seriesData = w.config.series[i].data;
          const pointIndex = seriesData.findIndex(point => point.x === x);
          if (pointIndex !== -1 && seriesData[pointIndex].y !== null && seriesData[pointIndex].y !== undefined) {
            let modeLabel = w.config.series[i].name;
            if (modeLabel.endsWith(':')) modeLabel = modeLabel.slice(0, -1);
            const color = w.config.series[i].color || '#40a57b';
            const value = seriesData[pointIndex].y;
            tooltipContent += `
              <div style="display:flex;text-align:center;align-items:center;gap:8px;padding:8px 10px;">
                <span style="display:inline-block;width:12px;height:12px;background:${color};border-radius:50%;margin-right:4px;"></span>
                <span style="color:#222;">${modeLabel}: ${value}</span>
              </div>
            `;
          }
        }
        
        return `
          <div style="min-width:200px;">
            <div style="background:#f5f6fa;padding:8px 0 8px 0;border-radius:8px 8px 0 0;text-align:center">
              ${formattedDate}
            </div>
            ${tooltipContent}
          </div>
        `;
      }
    },
    colors: Object.values(modeConfig).map(mode => mode.color),
    title: {
      text: "CHP Operational Mode Timeline",
      align: "left",
      style: {
        fontSize: "16px",
        fontWeight: "bold"
      }
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      onItemClick: {
        toggleDataSeries: false
      }
    }
  };

  return (
    <div style={{
      padding: 18,
      textAlign: "center",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
      borderRadius: 8,
      backgroundColor: "#fff",
      justifyContent: "center",
      alignItems: "center",
      alignSelf: "center",
    }}>
      <ReactApexChart options={options} series={seriesData} type="area" height={350} />
    </div>
  );
}



